import React, { useState, ChangeEvent, useCallback, useEffect, useRef } from "react";

import { AuthType, useAuthentication } from "@shared/contexts";
import { useOAuth2 } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";

import { NavigateFunction, useNavigate } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";
import { TextButton } from "@components/ui";


import { PagePaths } from "@shared/constants";
import { ExternalURL } from "@shared/constants";
import {
  ComposeURLWithGetQueryArguments,
  ComposeURLWithResponseParameters,
} from "@shared/utils";

import {
  PasswordRequest,
} from "../services/OAuth2Request";
import { useAuthError } from "@shared/hooks/useAuthError";
import OAuth2Service from "@features/authentication/services/OAuth2Service";
import { delayIfInRange, isSuccessTokenStepResponse, isRedirectionResponse, markStateAsUsed } from "@shared/utils";
import { isDevelopment } from "@shared/utils/environment";
import { TokenExpirationChecker } from "@shared/utils/token/TokenExpirationChecker";
import { RedirectionResponse, SuccessTokenStepResponse, OpenAPI } from "@shared/services";
import { useTranslation } from "react-i18next";
import { t_password } from "@i18n/usualTexts/t_password";
import i18n from "@i18n/index";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import {
  cleanFormDataErrors,
} from "@shared/utils";

function devLog(message: string, data?: unknown): void {
  if (isDevelopment()) {
    console.log(message, data ?? '');
  }
}

function buildSearchParams(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

type RedirectStrategy = () => Promise<string>;

async function tryRedirectStrategies(strategies: RedirectStrategy[]): Promise<void> {
  for (const strategy of strategies) {
    try {
      const url = await strategy();
      window.location.href = url;
      return;
    } catch (error) {
      console.error('[Password] Redirect strategy failed:', error);
    }
  }
  throw new Error(i18n.t('error.runtime.redirectFailed'));
}

interface FormData {
  password: {
    value: string;
    errors: Set<string>;
  };
}

const PasswordPage: React.FC = () => {
  const { t } = useTranslation();
  useDocumentTitle(t("login.password.pageTitle"));
  const navigate = useNavigate();
  const { handleOAuth2Error } = useAuthError();
  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const submittedResetRef = useRef<number | null>(null);
  const { currentToken: antiBotToken } = useRecaptchaContext();

  const { authData, setAuthData } = useAuthentication();
  const { oauth2Data, state } = useOAuth2();
  const [formData, setFormData] = useState<FormData>({
    password: {
      value: "",
      errors: new Set<string>(),
    },
  });

  const redirectToIdentity = useCallback(() => {
    setAuthData({ ...authData, step: 1, identityRequestResponse: undefined });
    navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY));
  }, [authData, setAuthData, navigate]);

  useEffect(() => {
    if (!authData.identityRequestResponse || !authData.username) {
      redirectToIdentity();
      return;
    }

    const identityResponse = authData.identityRequestResponse;
    if (!identityResponse) return;

    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = identityResponse.expiration_time <= (currentTime + 30);
    if (isExpired) redirectToIdentity();
  }, [authData, setAuthData, navigate, redirectToIdentity]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (submittedResetRef.current !== null) {
        cancelAnimationFrame(submittedResetRef.current);
      }
    };
  }, []);

  const handleFormData = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { value } = e.target;
    formData.password.value = value;
    setFormData({ ...formData });
    cleanFormDataErrors(formData);
  }, [formData]);

  const back = useCallback(() => {
    setAuthData({
      ...authData,
      step: 1,
    });

    navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY));
  }, [authData, setAuthData, navigate]);

  const handleTokenExpiration = useCallback(() => {
    console.log('[Password] Identity token expired, redirecting to identity page');
    navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY));
  }, [navigate]);

  const handlePasswordRequest = useCallback(async (identityToken: string) => {
    devLog('[Password] Starting password request');

    await delayIfInRange(authData.identityRequestResponse);

    const resolvedAntiBotToken = antiBotToken || undefined;
    const response = await PasswordRequest(
      oauth2Data,
      formData.password.value,
      identityToken,
      resolvedAntiBotToken
    );
    devLog('[Password] Password request response received');
    return response;
  }, [authData.identityRequestResponse, oauth2Data, formData.password.value, antiBotToken]);

  const logRedirectionResponse = useCallback((redirectionResponse: RedirectionResponse) => {
    devLog('[Password] Handling redirection response:', {
      location: redirectionResponse.location,
      statusCode: redirectionResponse.status_code,
      hasParameters: !!redirectionResponse.parameters,
      parameters: redirectionResponse.parameters,
      hasState: !!state,
    });
  }, [state]);

  const markStateAsUsedIfPresent = useCallback(() => {
    if (state) {
      devLog('[Password] Marking state as used');
      markStateAsUsed(state);
    }
  }, [state]);

  const attemptUrlComposition = useCallback(async (
    redirectionResponse: RedirectionResponse,
    logPrefix: string
  ): Promise<string> => {
    devLog(`[Password] ${logPrefix}...`);
    const url = await ComposeURLWithResponseParameters(redirectionResponse);
    devLog(`[Password] ${logPrefix} successfully:`, url);
    return url;
  }, []);

  const getBaseUrl = useCallback((): string => {
    return OpenAPI.BASE || window.location.origin;
  }, []);

  const cleanLocationPath = useCallback((location: string): string => {
    return location.startsWith('/') ? location.substring(1) : location;
  }, []);

  const constructManualUrl = useCallback((redirectionResponse: RedirectionResponse): string => {
    const baseUrl = getBaseUrl();
    const rawLocation = redirectionResponse.location || '';
    const cleanLocation = cleanLocationPath(rawLocation);
    const params = redirectionResponse.parameters || {};
    const queryString = buildSearchParams(params);
    const queryPart = queryString ? '?' + queryString : '';
    return `${baseUrl}/${cleanLocation}${queryPart}`;
  }, [getBaseUrl, cleanLocationPath]);

  const mergeOAuth2Parameters = useCallback((redirectionResponse: RedirectionResponse): RedirectionResponse => {
    const responseParams = redirectionResponse.parameters || {};
    const mergedParameters: Record<string, any> = { ...responseParams };
    
    // Define OAuth2 parameters to merge (response parameters take precedence)
    const oauth2ParamsToMerge: Record<string, string | undefined> = {
      client_id: oauth2Data.client_id,
      redirect_uri: oauth2Data.redirect_uri,
      scope: oauth2Data.scope,
      response_type: oauth2Data.response_type,
      code_challenge: oauth2Data.code_challenge,
      code_challenge_method: oauth2Data.code_challenge_method,
      state: state || undefined,
    };
    
    // Merge OAuth2 parameters if not already present in response
    Object.entries(oauth2ParamsToMerge).forEach(([key, value]) => {
      if (!mergedParameters[key] && value) {
        mergedParameters[key] = value;
      }
    });
    
    devLog('[Password] Merged OAuth2 parameters:', {
      originalParams: responseParams,
      mergedParams: mergedParameters,
      oauth2Data,
      hasState: !!state,
    });
    
    return {
      ...redirectionResponse,
      parameters: mergedParameters,
    };
  }, [oauth2Data, state]);

  const handleRedirectionResponse = useCallback(async (redirectionResponse: RedirectionResponse) => {
    logRedirectionResponse(redirectionResponse);
    markStateAsUsedIfPresent();
    
    const mergedResponse = mergeOAuth2Parameters(redirectionResponse);
    
    await tryRedirectStrategies([
      () => attemptUrlComposition(mergedResponse, 'Composing redirect URL'),
      () => attemptUrlComposition(mergedResponse, 'Attempting fallback URL composition'),
      () => {
        const manualUrl = constructManualUrl(mergedResponse);
        devLog('[Password] Manual redirect URL:', manualUrl);
        return Promise.resolve(manualUrl);
      },
    ]);
  }, [logRedirectionResponse, markStateAsUsedIfPresent, attemptUrlComposition, constructManualUrl, mergeOAuth2Parameters]);

  const logResponseAnalysis = useCallback((passwordPostResponse: any) => {
    devLog('[Password] Password response received, analyzing type...');
    devLog('[Password] Response structure:', {
      isSuccessTokenStepResponse: isSuccessTokenStepResponse(passwordPostResponse),
      isRedirectionResponse: isRedirectionResponse(passwordPostResponse),
      hasAccessToken: !!passwordPostResponse?.access_token,
      hasLocation: !!passwordPostResponse?.location,
      hasStatusCode: !!passwordPostResponse?.status_code,
      hasNextStep: !!passwordPostResponse?.next_step,
    });
  }, []);

  const logResponseTypeDetermination = useCallback((isSuccessResponse: boolean, isRedirectResponse: boolean) => {
    devLog('[Password] Response type determination:', {
      isSuccessResponse,
      isRedirectResponse,
      willNavigateToSecondFactor: isSuccessResponse,
      willHandleRedirect: isRedirectResponse,
    });
  }, []);

  const handleSuccessResponse = useCallback((passwordPostResponse: SuccessTokenStepResponse, newAccountData: AuthType) => {
    devLog('[Password] Response is SuccessTokenStepResponse, checking next_step');
    const updatedAuthData: AuthType = {
      ...newAccountData,
      passwordRequestResponse: passwordPostResponse,
    };
    setAuthData(updatedAuthData);
    
    const nextStep = passwordPostResponse.next_step || PagePaths.LOGIN.SECONDFACTOR;
    navigate(ComposeURLWithGetQueryArguments(nextStep));
  }, [setAuthData, navigate]);

  const handleRedirectResponse = useCallback(async (passwordPostResponse: RedirectionResponse, newAccountData: AuthType) => {
    devLog('[Password] Response is RedirectionResponse, handling redirect');
    const updatedAuthData: AuthType = {
      ...newAccountData,
      passwordRequestResponse: undefined,
    };
    setAuthData(updatedAuthData);
    await handleRedirectionResponse(passwordPostResponse);
  }, [setAuthData, handleRedirectionResponse]);

  const getValidIdentityToken = useCallback((): string | null => {
    if (TokenExpirationChecker.isTokenExpired(authData.identityRequestResponse, 30)) {
      handleTokenExpiration();
      return null;
    }
    return authData.identityRequestResponse?.access_token || null;
  }, [authData.identityRequestResponse, handleTokenExpiration]);

  const routePasswordResponse = useCallback(async (passwordPostResponse: any, newAccountData: AuthType) => {
    if (isSuccessTokenStepResponse(passwordPostResponse)) {
      handleSuccessResponse(passwordPostResponse as SuccessTokenStepResponse, newAccountData);
      return;
    }
    if (isRedirectionResponse(passwordPostResponse)) {
      await handleRedirectResponse(passwordPostResponse as RedirectionResponse, newAccountData);
      return;
    }
    console.error('[Password] Unexpected response type:', passwordPostResponse);
    throw new Error(i18n.t('error.runtime.unexpectedPasswordResponse'));
  }, [handleSuccessResponse, handleRedirectResponse]);

  const postFormDatas_and_navigate = useCallback(async (newAccountData: AuthType) => {
    const identityToken = getValidIdentityToken();
    if (!identityToken) {
      return;
    }

    devLog('[Password] Identity token is valid, proceeding with password request');

    const passwordPostResponse = await handlePasswordRequest(identityToken);
    logResponseAnalysis(passwordPostResponse);
    logResponseTypeDetermination(isSuccessTokenStepResponse(passwordPostResponse), isRedirectionResponse(passwordPostResponse));

    await routePasswordResponse(passwordPostResponse, newAccountData);
  }, [getValidIdentityToken, handlePasswordRequest, logResponseAnalysis, logResponseTypeDetermination, routePasswordResponse]);

  const handleSubmit = useCallback(async () => {
    // Set loading state immediately for instant UI feedback
    setIsLoading(true);
    setSubmitted(true);

    try {
      const step = 3;

      const newAccountData: AuthType = {
        ...authData,
        step: step,
      };

      const authenticationService: OAuth2Service = new OAuth2Service();
      authenticationService.checkPasswordFormat(formData.password.value);

      await postFormDatas_and_navigate(newAccountData);
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      handleOAuth2Error(
        "password",
        error,
        setServerErrorPopUp,
        formData.password.errors
      );

      // Removed unused newAccountData variable
    } finally {
      setIsLoading(false);

      setFormData({
        password: {
          ...formData.password,
        },
      });

      if (submittedResetRef.current !== null) {
        cancelAnimationFrame(submittedResetRef.current);
      }
      const startTime = performance.now();
      const check = () => {
        if (performance.now() - startTime >= 100) {
          setSubmitted(false);
          submittedResetRef.current = null;
        } else {
          submittedResetRef.current = requestAnimationFrame(check);
        }
      };
      submittedResetRef.current = requestAnimationFrame(check);
    }
  }, [authData, formData, postFormDatas_and_navigate, handleOAuth2Error]);

  return (
    <AuthenticationForm
      back={back}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
    >
      <div className="text-center">
        <h2 className="text-lg font-semibold mb-2 text-[var(--text-primary)]">
          {t(t_password.title, { username: authData.username })}
        </h2>
        <p className="text-[var(--text-secondary)]">
          {t(t_password.subtitle)}
        </p>
      </div>

      <FormField
        autofocus={true}
        name="password"
        type="password"
        value={formData.password.value}
        onChange={handleFormData}
        forgotPasswordText={t(t_password.forgotPasswordLink)}
        forgotPasswordAction={() =>
          toSendRecoveryPasswordLinkPage(navigate)
        }
        errors={formData.password.errors}
        required={true}
        submitted={submitted}
      >
        {t(t_password.passwordPlaceholder)}
      </FormField>

      <div className="text-justify">
        <p className="text-sm text-[var(--text-secondary)]">
          {t(t_password.securityWarning)}{" "}
          <TextButton url={ExternalURL.MORE_DETAILS}>
            {t(t_password.securityWarningLink)}
          </TextButton>
        </p>
      </div>
    </AuthenticationForm>
  );
};

function toSendRecoveryPasswordLinkPage(navigate: NavigateFunction) {
  navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.SENDRECOVERYPASSWORDLINK));
}

export default PasswordPage;